import React from 'react';
import {
  Modal,
  Box,
  IconButton,
  Backdrop,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

interface ProjectModalProps {
  open: boolean;
  onClose: () => void;
  projectComponent: React.ComponentType | null;
}

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: {
    xs: '90%',
    sm: '80%',
    md: '70%',
    lg: '60%',
  },
  maxWidth: '800px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  animation: 'modalFadeIn 0.3s ease-out',
  '@keyframes modalFadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translate(-50%, -50%) scale(0.9)',
    },
    '100%': {
      opacity: 1,
      transform: 'translate(-50%, -50%) scale(1)',
    },
  },
};


const ProjectModal: React.FC<ProjectModalProps> = ({ open, onClose, projectComponent: ProjectComponent }) => {
  if (!ProjectComponent) return null;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="project-modal-title"
      aria-describedby="project-modal-description"
      onKeyDown={handleKeyDown}
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            transition: 'backdrop-filter 0.3s ease-in-out, background-color 0.3s ease-in-out',
          },
        },
      }}
    >
      <Box sx={modalStyle}>
        {/* Header avec bouton fermer - FIXE */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0, // Empêche le rétrécissement
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Fermer la modal"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Contenu du projet spécifique - SCROLLABLE */}
        <Box 
          sx={{ 
            flex: 1, // Prend tout l'espace restant
            overflow: 'auto', // Permet le scroll
            mx: 2, // Marge horizontale pour décaler la scrollbar
            my: 1, // Petite marge verticale
            borderBottomLeftRadius: 6,
            borderBottomRightRadius: 6,
          }}
        >
          <Box sx={{ p: 3 }}>
            <ProjectComponent />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ProjectModal;
